/**
 * `createPrinting` — the only orchestrator. It wires the pure pieces together and holds the one
 * piece of state the flow has: the queue.
 *
 *   context snapshot → document plan → status claim → render → queue → transport → report
 *
 * Nothing here decides WHAT prints (documents do), HOW it is encoded (render does) or how it reaches
 * the printer (the host's transport does).
 */
import { DOCUMENTS, planSaleClosed } from "./documents";
import { createQueue, withTimeout } from "./queue";
import { renderPiece } from "./render";
import type {
  DocumentKind,
  InputByKind,
  JobResult,
  Plan,
  PrintContext,
  PrintOptions,
  PrintReport,
  Printing,
  PrintingDeps,
  Rasterizer,
  Skip,
  StatusResult,
} from "./types";

const DEFAULT_GAP_MS = 500;
const DEFAULT_TIMEOUT_MS = 15000;

const SILENT = Symbol("silent");

/**
 * The rasteriser is a port like the transport, and holds its caller the same way: one that never
 * answers (a WebView that never loaded) would keep its print request open forever. Each call gets
 * the job timeout; a late answer is ignored.
 */
function guardRasterizer(raster: Rasterizer | undefined, timeoutMs: number): Rasterizer | undefined {
  if (!raster) return undefined;
  const answered = async <T>(call: () => Promise<T> | T): Promise<T> => {
    const result = await withTimeout<T | typeof SILENT>(Promise.resolve().then(call), timeoutMs, SILENT);
    if (result === SILENT) {
      throw Object.assign(new Error(`the rasteriser did not answer within ${timeoutMs} ms`), { code: "TIMEOUT" });
    }
    return result;
  };
  return {
    toPixels: (html, widthPx) => answered(() => raster.toPixels(html, widthPx)),
    qrDataUrl: (text) => answered(() => raster.qrDataUrl(text)),
    barcodeDataUrl: (value) => answered(() => raster.barcodeDataUrl(value)),
  };
}

export function createPrinting(deps: PrintingDeps): Printing {
  const timeoutMs = deps.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const queue = createQueue(deps.transport, { gapMs: deps.gapMs ?? DEFAULT_GAP_MS, timeoutMs });
  const raster = guardRasterizer(deps.raster, timeoutMs);
  let jobSeq = 0;

  /** A claim's status write. A throw is v1's "the write failed" — it never rejects. */
  function writeClaim(entry: NonNullable<Plan["claim"]>): Promise<StatusResult> {
    return Promise.resolve()
      .then(() =>
        entry.of === "tickets" ? deps.status.tickets(entry.saleId, entry.infos) : deps.status.labels(entry.saleId, entry.infos),
      )
      .catch(() => "unreachable" as StatusResult);
  }

  /**
   * Claims, renders and QUEUES a plan's pieces, then returns — without waiting for them to print.
   * The returned promises settle as the jobs do. Queuing in plan order is what keeps a bill ahead of
   * its KOTs, and a drawer kick ahead of its bill.
   */
  async function enqueue(plan: Plan, report: PrintReport, options?: PrintOptions): Promise<Promise<JobResult>[]> {
    const skip = (entry: Skip) => {
      report.skipped.push(entry);
      options?.onSkip?.(entry);
    };
    plan.skipped.forEach(skip);

    // Strict v1: an automatic KOT — or a label the poller prints — prints only once its Completed
    // status is accepted. The PLANNER decides whether a claim is due: a poller's labels are claimed
    // even when none of them routed to a printer here, as v1 does, or the server lists them forever.
    if (plan.claim) {
      const written = writeClaim(plan.claim);
      const answer = await withTimeout<StatusResult | typeof SILENT>(written, timeoutMs, SILENT);
      if (answer === SILENT) {
        // v1 printed whenever the write finally answered — a slow server still gets its tickets,
        // after this report has gone. Their outcomes are nobody's to read, as in v1.
        void written
          .then(async (late) => {
            if (late === "accepted") await queuePieces(plan.pieces);
          })
          .catch(() => undefined);
      }
      report.claim = answer === SILENT ? "unreachable" : answer;
      if (report.claim !== "accepted") {
        skip({ reason: report.claim === "rejected" ? "claimRejected" : "claimUnreachable" });
        return [];
      }
    }
    return queuePieces(plan.pieces);
  }

  async function queuePieces(pieces: Plan["pieces"]): Promise<Promise<JobResult>[]> {
    const jobs: Promise<JobResult>[] = [];
    for (const piece of pieces) {
      // A count v1's `while (copies)` loop would have spent without printing (a channel whose
      // `billPrintCopies` is 0, a negative or fractional one). Rendering it first would lay the
      // receipt out for nothing — and on the multi-language template that means a rasteriser call,
      // which is a WebView round trip that can take the whole job timeout to answer.
      if (piece.copies <= 0) continue;
      const done = (outcome: JobResult["outcome"]): JobResult => ({
        document: piece.document,
        destination: piece.destination,
        outcome,
      });
      let rendered;
      try {
        rendered = await renderPiece(piece, deps.transport.accepts(piece.destination), raster);
      } catch (error: any) {
        const code = error?.code === "TIMEOUT" ? "TIMEOUT" : "RENDER_ERROR";
        rendered = { error: { ok: false as const, code, detail: String(error?.message ?? error) } };
      }
      if ("error" in rendered) {
        jobs.push(Promise.resolve(done(rendered.error)));
        continue;
      }
      // v1 order for a split HTML bill: every copy of page 1, then every copy of page 2
      for (const payload of rendered.payloads) {
        for (let copy = 0; copy < piece.copies; copy++) {
          jobs.push(queue.run({ id: `print-${++jobSeq}`, piece, payload }).then(done));
        }
      }
    }
    return jobs;
  }

  async function finish(report: PrintReport, jobs: Promise<JobResult>[]): Promise<PrintReport> {
    report.jobs.push(...(await Promise.all(jobs)));
    return report;
  }

  /**
   * A planner lays content out through `@dotpe/rista-library`, which throws on data it cannot read
   * — an item label whose item has neither barcode nor SKU, for one. v1 let that reach its caller:
   * on the POS it was an unhandled rejection, and inside the poller's loop it stopped polling. Here
   * it is one more reported skip, so the rest of the request — and the poller — carries on.
   */
  function plan<K extends DocumentKind>(kind: K, input: InputByKind[K], ctx: PrintContext): Plan {
    try {
      return DOCUMENTS[kind](input, ctx);
    } catch (error: any) {
      return { pieces: [], skipped: [{ reason: "planFailed", document: kind, detail: String(error?.message ?? error) }] };
    }
  }

  return {
    async print(kind, input, options) {
      const ctx = deps.context();
      const report: PrintReport = { jobs: [], skipped: [] };
      return finish(report, await enqueue(plan(kind, input, ctx), report, options));
    },

    async saleClosed(sale, options) {
      const ctx = deps.context();
      const report: PrintReport = { jobs: [], skipped: [] };
      const { bill, tickets } = planSaleClosed(sale, ctx);
      // the bill is QUEUED before the KOT claim goes out, not printed first — as v1
      const billJobs = bill ? await enqueue(plan("bill", bill, ctx), report, options) : [];
      const ticketJobs = await enqueue(plan("tickets", tickets, ctx), report, options);
      return finish(report, [...billJobs, ...ticketJobs]);
    },
  };
}
